/**
 * 基于 Web Audio API 的节拍器发声引擎。
 * 支持按音频时钟精确排期（when 参数），配合 lookahead 调度器实现无抖动节奏。
 *
 * 移动端（iOS Safari / Android Chrome）额外的坑这里都做了处理：
 * 1. 自动播放策略：上下文必须在用户手势的同步调用栈内创建 + resume，否则永久 suspended；
 * 2. iOS 私有状态 'interrupted'（来电、锁屏、切后台）不在标准枚举里，只判 'suspended' 会漏掉；
 * 3. resume() 的 Promise 可能在硬件真正启动前就 resolve，需要轮询确认；
 * 4. Android Chrome 有时把 Web Audio 路由到通话回路，表现为「有节拍但完全无声 / 音量极小」；
 * 5. 手机外放对极短促的高频脉冲还原很差，需要足够长的包络；另外 DynamicsCompressor 无 makeup gain，
 *    只会压低电平，抬响度要靠 tanh 软限幅；
 * 6. iOS 静音拨片会连带静音 Web Audio，靠一段 <audio> 静音循环把会话切到「播放」类别；
 * 7. 华为 / 荣耀的 ArkWeb 内核给 Web Audio 渲染流单独上了低增益（同一份满刻度采样走 <audio> 明显更响），
 *    这种设备把发声切到媒体通路（见 outputMode.ts / MediaClickOutput.ts），拍点排期仍用音频时钟。
 *
 * 点击音的包络与电平在 sounds/clickVoice.ts，两条通路共用，改那里才会同时生效。
 */
import { MediaClickOutput } from './MediaClickOutput'
import { createNoiseBuffer, createSoftClipShaper, scheduleClickNodes } from './clickVoice'
import {
  isMobileBrowser,
  resolveOutputMode,
  storeOutputMode,
  type SoundOutputMode,
} from './outputMode'

export type { SoundOutputMode }

export type AudioContextCtor = new () => AudioContext

declare global {
  interface Window {
    webkitAudioContext?: AudioContextCtor
  }
}

/** 等待 resume 真正生效的轮询间隔与上限（毫秒）；上限过长会让首次点击感觉发顿 */
const RESUME_POLL_MS = 40
const RESUME_TIMEOUT_MS = 250

/** 上下文是否已在推进时钟；iOS 的 'interrupted' 等私有状态一律视为未运行 */
function isRunningState(ctx: AudioContext): boolean {
  return (ctx.state as string) === 'running'
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** 页面是否在后台 / 息屏：这种状态下定时器会被节流到秒级 */
function isPageHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

/** 构造一段全零采样的 WAV（即静音），用于唤醒 iOS 的“播放”音频会话 */
function buildSilentWav(sampleRate: number, seconds: number): Blob {
  const frames = Math.max(1, Math.floor(sampleRate * seconds))
  const buffer = new ArrayBuffer(44 + frames * 2)
  const view = new DataView(buffer)
  const writeText = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }
  writeText(0, 'RIFF')
  view.setUint32(4, 36 + frames * 2, true)
  writeText(8, 'WAVE')
  writeText(12, 'fmt ')
  view.setUint32(16, 16, true) // fmt 块长度
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // 单声道
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byteRate
  view.setUint16(32, 2, true) // blockAlign
  view.setUint16(34, 16, true) // bitsPerSample
  writeText(36, 'data')
  view.setUint32(40, frames * 2, true)
  return new Blob([view], { type: 'audio/wav' })
}

export class MetronomeSound {
  private audioContext: AudioContext | null = null
  /** 点击音的汇流点，增益固定 1.0，只为给软限幅提供稳定的驱动电平 */
  private bus: GainNode | null = null
  /** 输出音量节点，位于软限幅之后，保证 setVolume 是线性缩放而不改变饱和特性 */
  private volumeGain: GainNode | null = null
  private noiseBuffer: AudioBuffer | null = null
  private volume = 1
  /** 承载静音循环的媒体元素，用来把 iOS 音频会话切到「播放」类别 */
  private silentAudio: HTMLAudioElement | null = null
  private silentUrl = ''
  /** 媒体元素通路（部分内核下比 Web Audio 直出更响） */
  private mediaOut = new MediaClickOutput()
  private outputMode: SoundOutputMode

  constructor() {
    this.outputMode = resolveOutputMode()
  }

  /** 是否支持 Web Audio（不支持时静默降级为纯视觉节拍器） */
  public static get isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!(window.AudioContext || window.webkitAudioContext)
    )
  }

  /** 首次创建上下文：搭好输出链并预生成噪声 buffer */
  private create(): AudioContext | null {
    const Ctor = window.AudioContext ?? window.webkitAudioContext
    if (!Ctor) return null
    const ctx = new Ctor()

    // 汇流 → tanh 软限幅 → 音量 → destination。
    // 不用 DynamicsCompressor：它在 Web Audio 里没有 makeup gain，只会把电平整体压低
    // （实测比软限幅低约 9dB）；固定 drive 的 tanh 饱和既能推响又不会逐拍抽吸。
    const bus = ctx.createGain()
    const shaper = createSoftClipShaper(ctx)
    const volumeGain = ctx.createGain()
    volumeGain.gain.value = this.volume
    bus.connect(shaper)
    shaper.connect(volumeGain)
    volumeGain.connect(ctx.destination)

    this.audioContext = ctx
    this.bus = bus
    this.volumeGain = volumeGain
    this.noiseBuffer = createNoiseBuffer(ctx)
    this.requestDefaultSink(ctx)
    return ctx
  }

  /**
   * Android Chrome 的 Web Audio 有时被路由到「通话回路」，用户侧表现为完全无声或音量极小。
   * 显式把输出设回默认设备。注意两点：
   * 1. 默认设备的合法 id 是空串，不是 'default'（写错会被判为非法设备变更而整个调用无效）；
   * 2. AudioContext.setSinkId 是 Chrome 110+ 才有的，华为等内核偏旧的浏览器不支持，只能特性检测到就跳过。
   */
  private requestDefaultSink(ctx: AudioContext) {
    if (!isMobileBrowser()) return
    const sinkCtx = ctx as AudioContext & {
      setSinkId?: (id: string) => Promise<void>
    }
    if (typeof sinkCtx.setSinkId !== 'function') return
    try {
      void sinkCtx.setSinkId('').catch(() => undefined)
    } catch {
      // 旧版本不接受该值，忽略
    }
  }

  /** 在用户手势内播放一帧静音，唤醒 iOS/Android 的音频硬件（移动端解锁的标准技巧） */
  private unlock(ctx: AudioContext) {
    const source = ctx.createBufferSource()
    source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate)
    source.connect(ctx.destination)
    source.start(0)
  }

  /**
   * iOS 侧边静音拨片会把 Web Audio 一起静音，表现为「手机完全没声音」。
   * 循环播一段不可闻的 <audio>，可让 Safari 把音频会话切到「播放」类别，
   * 声音改走媒体音量，既绕开拨片也更接近用户对节拍器的预期。
   * 必须在用户手势的同步调用栈内触发；失败不影响 Web Audio 主链路。
   * 只对移动端生效：桌面版挂一个循环媒体元素会多出标签页出声图标等副作用。
   */
  public startOutputSession() {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return
    if (!isMobileBrowser()) return
    if (!this.silentAudio) {
      // 无 Blob URL 能力（极旧内核）时直接放弃这一层优化
      if (typeof URL.createObjectURL !== 'function') return
      const sampleRate = this.audioContext?.sampleRate ?? 44100
      this.silentUrl = URL.createObjectURL(buildSilentWav(sampleRate, 0.5))
      const audio = new Audio(this.silentUrl)
      audio.loop = true
      audio.preload = 'auto'
      audio.setAttribute('playsinline', '')
      this.silentAudio = audio
    }
    void this.silentAudio.play().catch(() => {
      // 被自动播放策略拒绝时只是失去绕开拨片的能力，保持静默
    })
  }

  /** 停止静音循环，交还音频会话 */
  public stopOutputSession() {
    const audio = this.silentAudio
    if (!audio) return
    audio.pause()
    if (audio.currentTime !== 0) audio.currentTime = 0
  }

  /** resume 并轮询确认真正进入 running；手势外调用被拒绝时靠轮询兜住 */
  private async resume(ctx: AudioContext): Promise<boolean> {
    try {
      await ctx.resume()
    } catch {
      // 不在用户手势内调用会被拒绝，继续轮询等待状态翻转
    }
    const deadline = Date.now() + RESUME_TIMEOUT_MS
    while (!isRunningState(ctx) && Date.now() < deadline) {
      await sleep(RESUME_POLL_MS)
    }
    return isRunningState(ctx)
  }

  /**
   * 懒初始化 AudioContext：必须在用户手势的同步调用栈内首次调用，
   * 并主动 resume 以符合移动端自动播放策略（iOS Safari 尤其严格）。
   * @returns 上下文；即使最终未进入 running 也返回，由调用方用 running 判断是否可发声
   */
  public async ensureContext(): Promise<AudioContext | null> {
    const ctx = this.audioContext ?? this.create()
    if (!ctx) return null
    if (isRunningState(ctx)) return ctx
    this.unlock(ctx)
    await this.resume(ctx)
    return ctx
  }

  /**
   * 供外部在用户手势里补一次唤醒（切后台、锁屏、来电中断后回到页面）。
   * @returns 音频是否已可发声
   */
  public async resumeNow(): Promise<boolean> {
    const ctx = this.audioContext
    if (!ctx) return false
    if (isRunningState(ctx)) return true
    this.unlock(ctx)
    return this.resume(ctx)
  }

  /** 当前音频时钟时间（秒），未初始化时返回 0 */
  public get now(): number {
    return this.audioContext?.currentTime ?? 0
  }

  /** 音频链路是否已真正运行（时钟在推进）；false 时任何排期都不会发声 */
  public get running(): boolean {
    return !!this.audioContext && isRunningState(this.audioContext)
  }

  /** 暴露只读的 AudioContext，供调度器读取精确时钟 */
  public get audioContextOrNull(): AudioContext | null {
    return this.audioContext
  }

  /**
   * 在指定音频时钟时刻发声。
   * @param isAccent 是否重音（第一拍）
   * @param when 音频时钟时刻（秒），缺省为立即发声；过去的时刻会被钳制为立即
   */
  public playClick(isAccent = false, when?: number) {
    const ctx = this.audioContext
    const bus = this.bus
    if (!ctx || !bus || !this.noiseBuffer) return

    const t = Math.max(when ?? ctx.currentTime, ctx.currentTime)
    const wantMedia = this.outputMode === 'media'

    // 媒体通路：电平走系统媒体音量，拍点时刻仍然由音频时钟换算出来。
    // 页面不可见时不走它：后台定时器被节流到秒级，那样发出来的点击既迟到又对不上画面。
    if (wantMedia && this.mediaOut.ready && !isPageHidden()) {
      this.mediaOut.play(isAccent, (t - ctx.currentTime) * 1000)
      return
    }
    // 采样还没渲好就后台补渲染，同时本拍先走 Web Audio 直出（同一段合成，只是在这类机型上偏小）
    if (wantMedia) void this.mediaOut.prepare(ctx.sampleRate)
    scheduleClickNodes(ctx, bus, this.noiseBuffer, t, isAccent)
  }

  /** 当前发声通路 */
  public get currentOutputMode(): SoundOutputMode {
    return this.outputMode
  }

  /** 通路是否需要媒体元素采样已就绪 */
  public get mediaReady(): boolean {
    return this.mediaOut.ready
  }

  /** 渲染媒体通路要用的采样；上下文还没建时先建一个（须在用户手势内调用） */
  public async prepareMediaOutput(): Promise<boolean> {
    const ctx = this.audioContext ?? this.create()
    if (!ctx) return false
    return this.mediaOut.prepare(ctx.sampleRate)
  }

  /**
   * 切换发声通路并记住选择。两种模式都要跑一次准备 / 释放，所以下面不按「模式没变」提前返回：
   * 从 localStorage 恢复成 media 时池子还是空的，提前返回会让每一拍都静默回落到电平偏小的直出。
   * 渲染失败时保持原通路（以返回值为准）。
   */
  public async setOutputMode(mode: SoundOutputMode): Promise<SoundOutputMode> {
    if (mode === 'media') {
      if (!(await this.prepareMediaOutput())) return this.outputMode
      this.outputMode = 'media'
    } else {
      this.mediaOut.cancel()
      // 不用媒体通路就把元素和 objectURL 释放掉，白占内存没意义
      this.mediaOut.dispose()
      this.outputMode = 'webaudio'
    }
    storeOutputMode(mode)
    return this.outputMode
  }

  /** 丢弃已经排期、但还没到点的媒体通路点击（停止播放时调用） */
  public cancelPendingOutput() {
    this.mediaOut.cancel()
  }

  public setVolume(value: number) {
    this.volume = Math.max(0, Math.min(1, value))
    this.mediaOut.setVolume(this.volume)
    const gain = this.volumeGain
    if (!gain || !this.audioContext) return
    const t = this.audioContext.currentTime
    gain.gain.cancelScheduledValues(t)
    // 平滑过渡，避免调节音量时出现咔哒声
    gain.gain.setTargetAtTime(this.volume, t, 0.02)
  }

  public cleanup() {
    this.stopOutputSession()
    this.mediaOut.dispose()
    if (this.silentAudio) {
      this.silentAudio.src = ''
      this.silentAudio = null
    }
    if (this.silentUrl) {
      URL.revokeObjectURL(this.silentUrl)
      this.silentUrl = ''
    }
    if (this.audioContext) {
      void this.audioContext.close()
      this.audioContext = null
      this.bus = null
      this.volumeGain = null
      this.noiseBuffer = null
    }
  }
}
